import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../types";

export const TOWERS = 3;
export const TOWER_BLOCKS = 12;
/**
 * How many blocks a tower must already hold before the 1 can cap it. The towers
 * are dealt four high, so nobody can claim at the deal — and because every
 * stack must run in descending order, building this high while the other player
 * can pull blocks off the top is the actual game.
 */
export const TOWER_CLAIM_HEIGHT = TOWER_BLOCKS / 2;
/**
 * Moves without a claim before the game is called. Two careful players can
 * shuffle blocks forever rather than hand each other a cappable tower, so this
 * is the backstop that lets them stop — set high enough that a six-block build
 * has room to mature first, which 24 moves did not give it.
 */
export const TOWER_STALL_LIMIT = 36;

export interface TowerSwapState extends BaseState {
	kind: "towerswap";
	/** Each tower, bottom block first. A block is its number. */
	towers: number[][];
	/** Who claimed the winning tower by capping it. */
	claimed: (PlayerIndex | null)[];
	/** Moves played so far — the first cap ends the game, so nothing resets it. */
	sinceClaim: number;
	/**
	 * Blocks each player has placed onto each tower, kept even after a block
	 * moves away again. This is what settles a capless game: whoever did more
	 * of the building on the tallest tower earned it, which turns the old flat
	 * draw into a reason to build at all.
	 */
	placed: [number[], number[]];
	lastNote: string | null;
}

export interface TowerSwapMove {
	from: number;
	to: number;
}

/** A block may only sit on a bigger one — the rule that makes it a puzzle. */
function canPlace(block: number, tower: number[]): boolean {
	if (tower.length === 0) return true;
	return block < tower[tower.length - 1];
}

/**
 * Who a capless game belongs to: compare placements tower by tower, tallest
 * first, and the first difference decides it. Two careful players building the
 * same tower alternately split it exactly evenly more often than not, so a
 * single-tower comparison left half of all stalled games drawn — the cascade
 * lets the rest of the board break the tie.
 */
export function stallWinner(
	towers: readonly number[][],
	placed: readonly [number[], number[]],
): PlayerIndex | null {
	const byHeight = towers
		.map((tower, index) => ({ index, height: tower.length }))
		.sort((a, b) => b.height - a.height);
	for (const { index } of byHeight) {
		if (placed[0][index] !== placed[1][index]) {
			return placed[0][index] > placed[1][index] ? 0 : 1;
		}
	}
	return null;
}

export const towerSwap: GameDefinition<TowerSwapState, TowerSwapMove> = {
	id: "towerswap",
	category: "puzzle",
	title: "Tower Swap",
	emoji: "🗼",
	blurb: "Move blocks between towers — small on big only. Cap a tall tower with the 1 to win.",
	howTo: [
		"Move the top block of any tower onto another tower.",
		"A block can only go on a bigger one, or on an empty space.",
		"Build a tower six blocks high, then put the 1 on top to cap it.",
		"The first cap wins — if nobody caps, whoever built more of the tallest tower does.",
	],

	create(rng) {
		const blocks = rng.shuffle([...Array(TOWER_BLOCKS).keys()].map((n) => n + 1));
		const towers: number[][] = Array.from({ length: TOWERS }, () => []);
		// Deal biggest-first into each tower so every start is a legal stack.
		for (let i = 0; i < blocks.length; i++) towers[i % TOWERS].push(blocks[i]);
		for (const tower of towers) tower.sort((a, b) => b - a);

		return {
			kind: "towerswap",
			towers,
			claimed: Array<PlayerIndex | null>(TOWERS).fill(null),
			sinceClaim: 0,
			placed: [Array<number>(TOWERS).fill(0), Array<number>(TOWERS).fill(0)],
			lastNote: null,
			// The stall settle counts placements, and the second mover lays the
			// last block before the clock runs out — a real edge, so who gets it
			// is drawn by lot rather than handed to whoever sits in seat 1.
			turn: rng.int(2) as PlayerIndex,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const { from, to } = raw as { from?: unknown; to?: unknown };
		if (typeof from !== "number" || !Number.isInteger(from) || from < 0 || from >= TOWERS) {
			return null;
		}
		if (typeof to !== "number" || !Number.isInteger(to) || to < 0 || to >= TOWERS) return null;
		if (from === to) return null;
		return { from, to };
	},

	apply(state, player, move): MoveOutcome<TowerSwapState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");

		const source = state.towers[move.from];
		if (source.length === 0) return reject("That tower is empty.");
		const block = source[source.length - 1];
		if (!canPlace(block, state.towers[move.to])) {
			return reject("A block can only go on a bigger one.");
		}

		const towers = state.towers.map((tower) => [...tower]);
		towers[move.from].pop();
		towers[move.to].push(block);

		const claimed = [...state.claimed];
		const scores: [number, number] = [...state.scores];
		const placed: [number[], number[]] = [[...state.placed[0]], [...state.placed[1]]];
		placed[player][move.to] += 1;
		let note = `Moved block ${block}.`;
		let claimedNow = false;

		// Capping a tall enough tower with the smallest block wins outright.
		if (block === 1 && towers[move.to].length > TOWER_CLAIM_HEIGHT) {
			claimed[move.to] = player;
			scores[player] += 1;
			claimedNow = true;
			note = "Capped it — the tower is yours!";
		}

		// With no towers ever frozen, the smallest exposed block always has
		// somewhere legal to go, so the game can only end by cap or by stall.
		const sinceClaim = claimedNow ? 0 : state.sinceClaim + 1;
		const stalled = !claimedNow && sinceClaim >= TOWER_STALL_LIMIT;
		const finished = claimedNow || stalled;

		// A capless game goes to whoever laid more of the tallest tower, so all
		// that careful building still counts for something. A tie there falls
		// through to the next tower down; only a dead-even split everywhere is
		// a draw.
		let result: { winner: PlayerIndex | null } | null = null;
		if (claimedNow) {
			result = resultFromScores(scores);
		} else if (stalled) {
			const winner = stallWinner(towers, placed);
			result = { winner };
			note =
				winner === null
					? "Nobody capped, and you built every tower evenly — a draw."
					: "Nobody capped — most blocks on the tallest tower takes it!";
		}

		return {
			ok: true,
			state: {
				...state,
				towers,
				claimed,
				scores,
				sinceClaim,
				placed,
				lastNote: note,
				turn: finished ? null : otherPlayer(player),
				result,
			},
		};
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		const moves: TowerSwapMove[] = [];
		state.towers.forEach((tower, from) => {
			if (tower.length === 0) return;
			const block = tower[tower.length - 1];
			state.towers.forEach((other, to) => {
				if (to === from) return;
				if (canPlace(block, other)) moves.push({ from, to });
			});
		});
		return moves;
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};
